import { useState, useEffect } from "react";
import { Search, Bookmark, Sun, Moon, LogOut, X, BookOpen, Loader2 } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import CalendarJump from "./CalendarJump";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

import { api } from "../lib/api";

export default function Header({
  onSearch,
  filter,
  onFilterChange,
  onCalendarJump,
  isSyncing,
}) {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState([{ key: null, label: "All" }]);
  const navigate = useNavigate();

  useEffect(() => {
    if (showFilters) {
      api.get("/bookmarks/counts").then(({ data }) => {
        const dynamicFilters = Object.keys(data).map(k => ({ key: k, label: `${k} (${data[k]})` }));
        setFilters([{ key: null, label: "All" }, ...dynamicFilters]);
      }).catch(console.error);
    }
  }, [showFilters]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-40 backdrop-blur-md bg-background/70 border-b border-border"
      data-testid="app-header"
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="font-serif-journal text-2xl italic tracking-tight" data-testid="brand-logo">
            journal
          </span>
          {filter && (
            <button
              onClick={() => onFilterChange(null)}
              className="meta-label text-muted-foreground flex items-center gap-1 hover:text-foreground transition-colors"
              data-testid="active-filter-chip"
            >
              {filter} <X className="w-3 h-3" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1">
          {isSyncing && (
            <div className="flex items-center gap-1 mr-2 px-2 py-1 rounded bg-secondary/50 text-muted-foreground meta-label" title="Syncing notes to local device">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Syncing</span>
            </div>
          )}
          <CalendarJump onJump={onCalendarJump} />

          <button
            onClick={onSearch}
            className="p-2 rounded-md hover:bg-secondary transition-colors"
            aria-label="search"
            data-testid="open-search-button"
          >
            <Search className="w-4 h-4" />
          </button>

          <DropdownMenu open={showFilters} onOpenChange={setShowFilters}>
            <DropdownMenuTrigger asChild>
              <button
                className="p-2 rounded-md hover:bg-secondary transition-colors"
                aria-label="bookmarks"
                data-testid="open-bookmarks-filter-button"
              >
                <Bookmark className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="meta-label">
              <DropdownMenuLabel className="meta-label text-muted-foreground">filter by</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {filters.map((f) => (
                <DropdownMenuItem
                  key={f.label}
                  onClick={() => onFilterChange(f.key)}
                  data-testid={f.key ? `filter-option-${f.key.toLowerCase()}` : "filter-option-all"}
                  className="meta-label cursor-pointer"
                >
                  {f.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>



          <button
            onClick={toggle}
            className="p-2 rounded-md hover:bg-secondary transition-colors"
            aria-label="toggle theme"
            data-testid="theme-toggle-button"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="ml-1 w-8 h-8 rounded-full bg-secondary text-secondary-foreground meta-label flex items-center justify-center"
                aria-label="account"
                data-testid="user-menu-button"
              >
                {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="font-serif-journal italic">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate("/settings")}
                className="meta-label cursor-pointer"
              >
                <Search className="w-3 h-3 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={logout}
                className="meta-label cursor-pointer"
                data-testid="logout-button"
              >
                <LogOut className="w-3 h-3 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
